import { useAuth } from '../core/AuthContext';
import '../styles/userlist.css';
import React, { useState, useEffect, useCallback, useRef, memo } from 'react';
import axios from 'axios';
import '../styles/userlist.css';
import { Icon } from '@iconify/react';
import { getDepartmentIcon, getDepartmentName, getDepartmentColor, resolveDepartmentRef } from '../core/constants/departments';
import { useDepartments } from '../core/DepartmentsContext';

import NavHeader from "./NavHeader";
import Avatar from './Avatar';
import { format } from 'date-fns';
import '../styles/general.css';
import { URL } from '../core/config';
import { useLocation } from 'react-router-dom';
import usePagination from './hooks/usePagination';
import CustomAlert from './CustomAlert';
import CustomAlertWithButtons from './CustomAlertWithButtons';
import { hasPermission } from '../core/utils/permissionUtils';


const usersURL = URL + "/users";

// Memoized Waiting List Section Component
const WaitingListSection = memo(({
    loading,
    users,
    selectedUserId,
    toggleUserInfo,
    handleAccept,
    handleReject,
    formatDateCustom,
    currentPage,
    totalPages,
    goToNextPage,
    goToPreviousPage,
    totalUsers,
    setAlertActionInfo,
    catalog,
}) => {
    return (
        <div className='user-list-main-section'>
            {loading ? (
                <div style={{ textAlign: 'center', padding: '20px' }}>Loading...</div>
            ) : (
                <>
                    <ul className="waiting-list">
                        {users.length > 0 ? (
                            users.map((user) => (
                                <li key={user._id} className="waiting-list-item" onClick={() => toggleUserInfo(user._id)}>
                                    <div className="waiting-list-item-row">
                                        <div className="waiting-list-item-avatar-wrapper">
                                            <Avatar
                                                user={user}
                                                style={{ border: '2px solid rgba(0,0,0,0.6)' }}
                                            />
                                        </div>
                                        <div className="waiting-list-item-info">
                                            <p className="waiting-list-item-name">
                                                {user.fullName} {user.nickname ? `"${user.nickname}"` : ''}
                                            </p>
                                            <p className="waiting-list-item-position">{user.position} · {user.membershipType}</p>
                                            <p className="waiting-list-item-departments">
                                                {(user.departments || []).map((department, index) => (
                                                    <span
                                                        key={index}
                                                        className="user-item-department"
                                                        style={{ '--department-color': getDepartmentColor(department, catalog) }}
                                                    >
                                                        <div className="dep-icon">{getDepartmentIcon(department, {}, catalog)}</div>
                                                        <div className="dep-name">{getDepartmentName(resolveDepartmentRef(department, catalog))}</div>
                                                    </span>
                                                ))}
                                            </p>
                                        </div>
                                        <div className="visible-section-buttons">
                                            <button
                                                className='waiting-list-button'
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleAccept(user._id);
                                                }}
                                            >
                                                <Icon icon="material-symbols:check" />
                                                <p>Accept</p>
                                            </button>
                                            <button
                                                className='waiting-list-button'
                                                onClick={(e) => {
                                                    e.stopPropagation();

                                                    // Show confirmation alert with CustomAlertWithButtons
                                                    setAlertActionInfo({
                                                        show: true,
                                                        title: 'Do you want to discard this user?',
                                                        type: 'error',
                                                        message: 'Are you sure you want to discard all information about this user? This cannot be undone and this account would need to be created from scratch again.',
                                                        btn1Action: () => {
                                                            // Just close the alert
                                                            setAlertActionInfo((prevState) => ({
                                                                ...prevState,
                                                                show: false,
                                                            }));
                                                        },
                                                        btn2Action: () => {
                                                            // Handle Reject if confirmed
                                                            handleReject(user._id);
                                                            // Close the alert after navigation
                                                            setAlertActionInfo((prevState) => ({
                                                                ...prevState,
                                                                show: false,
                                                            }));
                                                        },
                                                        btn1Text: 'No, not now',
                                                        btn2Text: 'Yes, delete it',
                                                    });


                                                }}
                                            >
                                                <Icon icon="bitcoin-icons:cross-filled" />
                                                <p>Reject</p>
                                            </button>
                                        </div>
                                    </div>

                                    {/* Toggleable extra user info */}
                                    {selectedUserId === user._id && (
                                        <div className="waiting-list-item-details">
                                            {user.firstLastName && <p><span>Short Name:</span> {user.firstLastName} </p>}
                                            {user.nickname && <p><span>Nickname:</span> {user.nickname} </p>}
                                            <p><span>Birthday:</span> {formatDateCustom(user.birthDate)} </p>
                                            <p><span>Nationality:</span> {user.nationality}</p>
                                            <p><span>Naturality:</span> {user.naturality}</p>
                                            <p><span>Professional Status:</span> {user.professionalStatus}</p>
                                            <br />
                                            <p><span>Personal Email:</span> {user.personalEmail}</p>
                                            <p><span>ESN Email:</span> {user.institutionalEmail}</p>
                                            <p><span>Address:</span> {user.address}</p>
                                            <br />
                                            <p><span>Emergency Contact Name:</span> {user.emergencyName} {user.emergencyRelationship && (<i>{user.emergencyRelationship}</i>)}</p>
                                            <p><span>Emergency Contact Phone:</span> {user.emergencyPhone}</p>
                                            <br />
                                            <p><span>Mobility:</span> {user.mobilityPlace} {user.mobilityProgram && (<i>{user.mobilityProgram}</i>)}</p>
                                            <p><span>Date joined ESN:</span> {formatDateCustom(user.dateJoined)}</p>
                                        </div>
                                    )}
                                </li>
                            ))
                        ) : (
                            <li className="no-results" style={{ textAlign: 'center', padding: '20px' }}>
                                No users match your search criteria.
                            </li>
                        )}
                    </ul>

                    {/* Pagination Controls moved inside the memoized component */}
                    {!loading && totalUsers > 0 && (
                        <div className="pagination-controls" style={{
                            display: 'flex',
                            justifyContent: 'center',
                            margin: '20px 0'
                        }}>
                            <button
                                className="button"
                                onClick={goToPreviousPage}
                                disabled={currentPage === 1}
                                style={{
                                    marginRight: '10px',
                                    opacity: currentPage === 1 ? 0.5 : 1
                                }}
                            >
                                <i className="uil uil-angle-left-b"></i> Previous
                            </button>

                            <span style={{ margin: '0 15px', lineHeight: '35px' }}>
                                Page {currentPage} of {totalPages || 1}
                            </span>

                            <button
                                className="button"
                                onClick={goToNextPage}
                                disabled={currentPage === totalPages || users.length === 0}
                                style={{
                                    marginLeft: '10px',
                                    opacity: currentPage === totalPages || users.length === 0 ? 0.5 : 1
                                }}
                            >
                                Next <i className="uil uil-angle-right-b"></i>
                            </button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
});


function HRList() {
    const { user, loading: authLoading } = useAuth(); // Get user and loading state from AuthContext. Renamed to avoid conflict
    const authContext = useAuth();
    const contextUser = authContext.user;
    const { departments: catalog } = useDepartments();
    const location = useLocation();

    const [users, setUsers] = useState([]);
    const [error, setError] = useState(null);
    const [selectedUserId, setSelectedUserId] = useState(null);
    const [loading, setLoading] = useState(true);

    // State for search and pagination
    const [usersPerPage] = useState(20);
    const [searchQuery, setSearchQuery] = useState('');
    const [totalUsers, setTotalUsers] = useState(0);
    const searchInputRef = useRef(null);
    const fetchRequestRef = useRef(0);
    const { currentPage, setCurrentPage, totalPages, goToNextPage, goToPreviousPage } = usePagination(usersPerPage, totalUsers);

    // For CustomAlert
    const [alertInfo, setAlertInfo] = useState({
        show: false,
        type: '',
        message: ''
    });

    const [alertActionInfo, setAlertActionInfo] = useState({
        show: false,
        title: '',
        type: '',
        message: '',
        btn1Action: () => { },
        btn2Action: () => { },
        btn1Text: '',
        btn2Text: ''
    });

    const formatDateCustom = useCallback((dataString) => {
        return dataString ? format(new Date(dataString), 'dd MMM yyyy') : '';
    }, []);

    const debouncedSearch = useCallback(
        (query) => {
            setSearchQuery(query);
            setCurrentPage(1); // Reset to page 1 when search query changes
        },
        [setCurrentPage]
    );

    const toggleUserInfo = useCallback((userId) => {
        setSelectedUserId(selectedUserId === userId ? null : userId);
    }, [selectedUserId]);

    const fetchWaitingUsers = useCallback(async ({ page = currentPage, search = searchQuery } = {}) => {
        const requestId = ++fetchRequestRef.current;

        try {
            setLoading(true);
            const params = new URLSearchParams();
            params.append('page', page);
            params.append('limit', usersPerPage);

            if (search) {
                params.append('search', search);
            }

            const response = await axios.get(`${usersURL}/waitinglist?${params.toString()}`, {
                headers: { 'Cache-Control': 'no-cache' },
            });

            if (requestId !== fetchRequestRef.current) {
                return;
            }

            setUsers(response.data.users || []);
            setTotalUsers(response.data.total || 0);
            setError(null);
        } catch (fetchError) {
            if (requestId !== fetchRequestRef.current) {
                return;
            }

            setError(fetchError);
            setUsers([]);
            setTotalUsers(0);
        } finally {
            if (requestId === fetchRequestRef.current) {
                setLoading(false);
            }
        }
    }, [currentPage, searchQuery, usersPerPage]);

    // Memoize handlers for accept and reject actions
    const handleAccept = useCallback(async (userId) => {
        try {
            const response = await axios.patch(`${usersURL}/${userId}`, { loginActive: true });
            if (response.status === 200 && response.data?.loginActive === true) {
                setUsers((prevUsers) => prevUsers.filter((entry) => String(entry._id) !== String(userId)));
                setTotalUsers((prevTotal) => Math.max(prevTotal - 1, 0));
                setAlertInfo({
                    show: true,
                    type: 'success',
                    message: 'The user was successfully accepted into ESN Lisboa!'
                });
                return;
            }

            setAlertInfo({
                show: true,
                type: 'error',
                message: 'The user could not be approved. Please try again or contact an administrator.'
            });
            await fetchWaitingUsers();
        } catch (error) {
            console.error('Error accepting user:', error);
            setAlertInfo({
                show: true,
                type: 'error',
                message: `The user could not be accepted. Please try again. Error: ${error.message}`
            });
        }
    }, [fetchWaitingUsers]);

    const handleReject = useCallback(async (userId) => {
        try {
            const response = await axios.delete(`${usersURL}/${userId}`);
            if (response.status === 200) {
                setUsers((prevUsers) => prevUsers.filter((entry) => String(entry._id) !== String(userId)));
                setTotalUsers((prevTotal) => Math.max(prevTotal - 1, 0));

                setAlertInfo({
                    show: true,
                    type: 'success',
                    message: 'The user was successfully eliminated!'
                });
            }
        } catch (error) {
            console.error('Error rejecting user:', error);
            setAlertInfo({
                show: true,
                type: 'error',
                message: `Error rejecting the user: ${error.message}. Please try again.`
            });
        }
    }, []);

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            fetchWaitingUsers();
        }, searchQuery ? 500 : 0);

        return () => clearTimeout(timeoutId);
    }, [currentPage, searchQuery, location.key, fetchWaitingUsers]);

    // Focus management for search input
    useEffect(() => {
        if (!loading && searchQuery && searchInputRef.current) {
            if (document.activeElement !== searchInputRef.current) {
                searchInputRef.current.focus();
            }
        }
    }, [loading, searchQuery]);

    if (authLoading) return <div>Loading...</div>;
    if (error) return <div>Error: {error.message}</div>;


    // Log the user object for debugging
    // console.log('User object from AuthContext:', contextUser, authLoading);

    // Show loading indicator while fetching user data
    if (authLoading) {
        return <div>Loading...</div>;  // Display a loading indicator while loading is true
    }

    // Check if the user object is defined and not empty
    if (!user || Object.keys(contextUser).length === 0) {
        return <div>No user data available.</div>;  // Display a message if no user data is available
    }
    // console.log('User acess:', contextUser.profile);

    // Check if user has permission to manage the waiting list
    if (!hasPermission(contextUser, 'MANAGE_WAITING_APPROVAL_LIST')) {
        return (
            <div>
                <div>You do not have permission to view this page.</div>
                <div>
                    <p>Your details:</p>
                    <ul>
                        <li>Email: {contextUser.email || 'Email not available'}</li>
                        <li>Name: {contextUser.name || 'Name not available'}</li>
                        <li>Login active: {contextUser.loginActive === true ? 'Yes' : 'No'}</li>
                    </ul>
                </div>
            </div>
        );
    }

    // ------------------------ Actual HTML page ------------------------
    return (
        <div className='page-container'>

            <NavHeader />

            <h1 className='page-title'>Waiting for Approval</h1>

            <div className="user-list-container waiting-list-page">

                <div className='user-list-lateral-section'>
                    <div className='search-notification-layout' style={{ margin: '10px 0' }}>
                        <div className="search-bar">
                            <input
                                type="text"
                                placeholder="Search..."
                                value={searchQuery}
                                onChange={(e) => debouncedSearch(e.target.value)}
                                ref={searchInputRef}
                            />
                            <i className="uil uil-search search-icon"></i>
                        </div>
                    </div>
                </div>

                {/* Use the memoized component */}
                <WaitingListSection
                    loading={loading}
                    users={users}
                    selectedUserId={selectedUserId}
                    toggleUserInfo={toggleUserInfo}
                    handleAccept={handleAccept}
                    handleReject={handleReject}
                    formatDateCustom={formatDateCustom}
                    currentPage={currentPage}
                    totalPages={totalPages}
                    goToNextPage={goToNextPage}
                    goToPreviousPage={goToPreviousPage}
                    totalUsers={totalUsers}
                    setAlertActionInfo={setAlertActionInfo}
                    catalog={catalog}
                />
            </div>
            {/* Alert that shows upon event submission */}
            {alertInfo.show && (
                <CustomAlert
                    type={alertInfo.type}
                    message={alertInfo.message}
                    autoDismiss={true}
                    timeout={4000}
                    onClose={() => setAlertInfo({ show: false, type: '', message: '' })}
                />
            )}

            {alertActionInfo.show && (
                <CustomAlertWithButtons
                    title={alertActionInfo.title}
                    type={alertActionInfo.type}
                    message={alertActionInfo.message}
                    onButton1Click={alertActionInfo.btn1Action}
                    onButton2Click={alertActionInfo.btn2Action}
                    button1Text={alertActionInfo.btn1Text}
                    button2Text={alertActionInfo.btn2Text}
                />)}
        </div>



    );
}

export default HRList;
